import {AjaxP, DOM, Module, TCFHandler} from '@mamdev/cad.static-next-lib';
import {render} from 'lit';

import {ExternalEmbedSettings} from '../../ts/services/externalembedsettings';
import IframeHandler from './iframehandler';
import {mask} from './templates/mask';
import {videoExternal} from './templates/videoExternal';
import {opinary} from './templates/opinary';
import {footer} from './templates/footer';
import Tracking2 from '../../ts/services/tracking2';
import Darkmode from '../../ts/services/darkmode';

/**
 * Externalembed
 */
export default class Externalembed extends Module {
  name_ = 'externalembed';

  private readonly externalEmbedSettings: ExternalEmbedSettings;
  private readonly storageKey = 'showExternalEmbed';

  private site = '';
  private dataUrl = '';
  private embedType = '';
  private dpUrl = '';
  private optinRequired = false;
  private storageAvailable = true;
  private externalEmbedPageUrl: string;

  contentContainer: HTMLElement | null;
  footerContainer: HTMLElement | null;
  iframe: HTMLIFrameElement | null;

  static attachTo(root: HTMLElement): Externalembed {
    return new Externalembed(root);
  }

  constructor(root: HTMLElement) {
    super(root);
    this.externalEmbedSettings = new ExternalEmbedSettings(this.storageKey);
  }

  /** Starts the module */
  start() {
    try {
      // check lS
      this.storageAvailable = this.localStorageUsable();
      if (!this.storageAvailable) {
        this.logError('localStorage not usable!');
        return;
      }
      this.init();
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  localStorageUsable(): boolean {
    try {
      const tk = '__LS_TEST__';
      window.localStorage.setItem(tk, '1');
      window.localStorage.removeItem(tk);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Initializing
   */
  init() {
    this.site = this.getSite();
    this.dataUrl = this.getDataUrl();
    this.embedType = this.getEmbedType();
    this.optinRequired = this.isOptinRequired();
    this.dpUrl = window.ui.links.thirdPartyPrivacy || '#';
    this.externalEmbedPageUrl = window.ui.links.externalEmbedSettings || '#';

    this.updateStorageKey();

    if (this.embedType) {
      this.contentContainer = (this.root_.getElementsByClassName('externalembed__content')[0] as HTMLElement);
      this.footerContainer = (this.root_.getElementsByClassName('externalembed__footer')[0] as HTMLElement);

      if (this.isAutoResize()) {
        IframeHandler.initialize();
      }

      this.initView();
    }

    this.subscribe('externalembed:show', data => {
      this.showEmbeddedContent(data as {site: string});
    });

    this.subscribe('externalembed:remove', data => {
      if (data && (data as {site: string}).site != this.site) {
        return;
      }
      this.resetFooterContainer();
      this.showMask();
    });

    this.subscribe('externalembed:loaded', (data: unknown) => {
      const payload = data as {url: string; contentHeight: string};
      if (this.iframe?.src) {
        if (this.iframe.src.includes(payload.url)) {
          this.iframe.style.height = payload.contentHeight + 'px'; // add room for breath
        }
      }
    });
  }

  initView() {
    // initial display
    if (!this.externalEmbedSettings.userOpttedIn(this.site) && this.embedType !== 'opinary' && this.optinRequired) {
      this.showMask();

      const site = this.getSite();
      const embedType = this.getEmbedType();
      const type = embedType == 'socialmedia' ? 'feed' : 'iframe';

      // for non opinary embeds, we want to trigger expose once the mask is shown
      // because we are always sure the content would be shown
      Tracking2.callTealiumLink({
        componentPath: `${this.componentPath}.externalembed.${site}-${type}`,
        componentName: `${site}-${type}`,
        businessEventType: 'expose'
      });
    } else if (this.site === 'civey') {
      // Civey now works with its own permission instead of the opt-in (CADNPCA-8376)
      if (TCFHandler.getPermission('civey')) {
        this.showEmbeddedContent();
      } else {
        this.showMask();
      }

      const site = this.getSite();
      const embedType = this.getEmbedType();
      const type = embedType == 'socialmedia' ? 'feed' : 'iframe';

      // for non opinary embeds, we want to trigger expose once the mask is shown
      // because we are always sure the content would be shown
      Tracking2.callTealiumLink({
        componentPath: `${this.componentPath}.externalembed.${site}-${type}`,
        componentName: `${site}-${type}`,
        businessEventType: 'expose'
      });
    } else {
      this.showEmbeddedContent();

      if (this.embedType === 'opinary' && this.getSite() === 'opinary') {
        if (this.footerContainer?.parentNode) {
          this.footerContainer.parentNode.removeChild(this.footerContainer);
        }
      }
    }
  }

  /**
   * showMask
   */
  showMask() {
    if (this.contentContainer) {
      if (this.embedType) {
        const title = this.root_.getAttribute('data-title') ?? '';
        const containerFragment = DOM.fromHtml('');
        render(mask(this.site, title, this.dpUrl, this.externalEmbedPageUrl), containerFragment);
        this.contentContainer.innerHTML = '';
        this.contentContainer.appendChild(containerFragment);
      }

      // set event for showing embedded content
      const btn = this.contentContainer.getElementsByClassName('externalembed__optin')[0];
      if (btn) {
        btn.addEventListener('click', () => {
          this.externalEmbedSettings.optInUser(true, this.site);
          this.publish('externalembed:show', {site: this.site});
          this.createPixel(this.site, true);
          this.sendTrackingEvent(this.site, true);
        });
      }

      let toggleState = false;
      const toggle = this.contentContainer.querySelector<HTMLInputElement>('.externalembed__optin-toggle input[type="checkbox"]');
      const ball = this.contentContainer.querySelector<HTMLElement>('.externalembed__optin-toggle .animate');

      if (toggle && ball) {
        toggle.addEventListener('change', e => {
          toggleState = (e.target as HTMLInputElement).checked;
        });

        ball.addEventListener('transitionend', e => {
          console.log(e);
          if (toggleState) {
            this.externalEmbedSettings.optInUser(true, this.site);
            this.publish('externalembed:show', {site: this.site});

            if (e.propertyName == 'transform') {
              // the transitionend event if fired once for all the css property that was transitioned
              // so we check for just one property in this case the transform.
              this.createPixel(this.site, true);
              this.sendTrackingEvent(this.site, true);
            }
          }
        });
      }
    }
  }

  showEmbeddedContent(data?: {site: string}) {
    if (data && (data.site !== this.site)) {
      return;
    }

    if (this.dataUrl.length > 0) {
      if (Darkmode.isActivated()) {
        if (this.site == 'twitter') {
          const url = new URL(this.dataUrl);
          url.searchParams.append('theme', 'dark');
          this.dataUrl = url.toString();
        } else if (this.site == 'heimspiel') {
          this.dataUrl += 'darkmode_allowed/';
        }
      }

      if (this.embedType === 'opinary') {
        const opts = {
          'credentials': 'include'
        };

        // get TCString and fetch opinary data
        TCFHandler.getTCString(resp => {
          AjaxP.getJSON(this.dataUrl + '&gdpr=1&gdpr_consent=' + (resp as string), opts).then((requestData: unknown) => {
            const data = requestData as {pollURL: string};
            if (data?.pollURL && this.contentContainer) {
              render(opinary(data.pollURL), this.contentContainer);

              // special case because of how opinarys are inserted into the page,
              // we trigger their expose event here
              Tracking2.callTealiumLink({
                componentPath: `${this.componentPath}.externalembed.opinary-iframe`,
                componentName: 'opinary-iframe',
                businessEventType: 'expose'
              });
            } else {
              console.log('NO data available');
              this.root_.classList.add('collapse');
            }
          }).catch(e => this.logUnknownError(e));
        });
      } else if (this.embedType === 'externalvideo') {
        if (!this.contentContainer) return;

        const containerFragment = DOM.fromHtml('');
        render(videoExternal(this.dataUrl), containerFragment);
        this.contentContainer.innerHTML = '';
        this.contentContainer.appendChild(containerFragment);

        this.showFooter();
      } else {
        // Use a sandboxed iframe to contain embedded contents CADNPCA-4935
        const iframe = document.createElement('iframe');
        iframe.className = 'eesb';
        iframe.setAttribute('scrolling', 'no');
        iframe.setAttribute('frameborder', '0');

        iframe.setAttribute('src', this.dataUrl);

        if (this.site !== 'facebook') {
          const sandboxAttr = this.root_.getAttribute('data-sandbox') ?? 'allow-popups allow-same-origin allow-scripts';
          iframe.setAttribute('sandbox', sandboxAttr);
        }

        const allowAttr = this.root_.getAttribute('data-allow');
        if (allowAttr) {
          // this function is not working on firefox: iframe.setAttribute('allow', allowAttr);
          // this old legacy attribute works on chrome and firefox...
          iframe.setAttribute('allowfullscreen', 'true');
        }

        const styleAttr = this.root_.getAttribute('data-style');
        if (styleAttr) {
          iframe.setAttribute('style', styleAttr);
        }

        if (this.isAutoResize()) {
          iframe.classList.add('autoresize');
        }

        this.iframe = iframe;
        if (this.contentContainer) {
          this.contentContainer.innerHTML = '';
          this.contentContainer.appendChild(iframe);
        }

        // setup footer for certain sites
        const allowedList = ['dpa', 'twitter', 'facebook', 'instagram', 'pinterest',
          'twenty_three_degrees', 'heimspiel'];
        if (allowedList.includes(this.site.toLowerCase())) {
          this.showFooter();
        }

        this.publish('externalembed:iframeLoad', null);
      }
    }
  }

  showFooter() {
    if (this.footerContainer && this.optinRequired) {
      const containerFragment = DOM.fromHtml('');
      render(footer(this.site, this.dpUrl, this.externalEmbedPageUrl), containerFragment);
      this.footerContainer.innerHTML = '';
      this.footerContainer.appendChild(containerFragment);

      let toggleState = false;
      const toggle = this.footerContainer.querySelector<HTMLInputElement>('.externalembed__optin-toggle input[type="checkbox"]');
      const ball = this.footerContainer.querySelector<HTMLElement>('.externalembed__optin-toggle .animate');

      if (toggle && ball) {
        toggle.checked = this.externalEmbedSettings.userOpttedIn(this.site);

        toggle.addEventListener('change', e => {
          toggleState = (e.target as HTMLInputElement).checked;
        });

        ball.addEventListener('transitionend', e => {
          if (!toggleState) {
            this.externalEmbedSettings.optInUser(false, this.site);
            this.publish('externalembed:remove', {site: this.site});

            if (e.propertyName == 'transform') {
              // the transitionend event if fired once for all the css property that was transitioned
              // so we check for just one property in this case the transform property.
              this.createPixel(this.site, false);
              this.sendTrackingEvent(this.site, false);
            }
          }
        });
      }
    }
  }

  resetFooterContainer() {
    if (this.footerContainer) {
      this.footerContainer.innerHTML = '';
    }
  }

  get componentPath() {
    return this.root_.getAttribute('data-component-path') ?? '';
  }

  getSite() {
    return this.root_.getAttribute('data-site') ?? '';
  }

  getDataUrl(): string {
    return this.root_.getAttribute('data-url') ?? '';
  }

  getEmbedType() {
    return this.root_.getAttribute('data-embed-type') ?? '';
  }

  isOptinRequired() {
    return this.root_.getAttribute('data-requires-optin') === 'true';
  }

  isAutoResize() {
    return this.embedType === 'iframe' || this.site === 'heimspiel' || this.site === 'unicef';
  }

  updateStorageKey() {
    if (!this.storageAvailable) {
      return;
    }

    try {
      const oldStorageKey = 'showSocialEmbed';
      const oldStorageValue = window.localStorage.getItem(oldStorageKey);
      if (oldStorageValue) {
        window.localStorage.setItem(this.storageKey, oldStorageValue);
        window.localStorage.removeItem(oldStorageKey);
      }
    } catch (e) {
      this.logUnknownError(e);
    }
  }

  createPixel(site: string, optIn: boolean) {
    const clickname = '3rdparty-' + site + '-' + (optIn ? 'optin' : 'optout');
    const data = {
      'clickname': '&clickname=' + clickname,
      'click1': '&click1=undef',
      'click2': '&click2=undef',
      'click3': '&click3=undef',
      'click4': '&click4=undef'
    };
    this.publish('externalembed:optin', data);

    return data;
  }

  sendTrackingEvent(site: string, optIn: boolean) {
    const optInStr = optIn ? 'optin' : 'optout';
    const type = this.embedType == 'socialmedia' ? 'feed' : 'iframe';

    Tracking2.callTealiumLink({
      businessEventType: 'userAction',
      componentPath: `${this.componentPath}.externalembed_${optInStr}-toggle.${site}-${type}`,
      componentName: `${site}-${type}.${optInStr}`,
      elementTagName: 'div',
      eventType: 'mousedown'
    });
  }
}
